import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import DateDisplay from '@app/src/components/DateDisplay'
import { useAssessmentMetrics } from '@app/src/hooks/useAssessmentMetrics'
import useAssessmentQuestionnaireLink from '@app/src/hooks/useAssessmentQuestionnaireLink'
import Assessment from '@app/src/types/assessment'
import { AssessmentType, ResponseItem } from '@app/src/types/resourceExplorer'
import { Box, Breadcrumbs, CircularProgress, Link, Paper, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Operators } from '../../../ResourceCollection/Filters/useFilters'
import TransparencyStandardAssessmentDetailedReview from './TransparencyStandardAssessmentDetailedReview'

const staleTime = 60000

const TransparencyStandardAssessmentPage: React.FC = () => {
  const { formatMessage } = useIntl()
  const { id } = useParams<{ id: string }>()

  const { items: fetchedAssessments } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection, id],
    endpoint: endpoints.assessmentFilterOwnOrganization,
    payload: {
      filter: [
        {
          name: 'id',
          filters: [{ operator: Operators.EqualTo, value: id }],
        },
      ],
      include: ['assessmentTemplate.rules'],
    },
    options: {
      enabled: Boolean(id),
      staleTime,
    },
  })

  const assessment = fetchedAssessments?.[0]

  const assessmentTemplateId = assessment?.assessmentTemplateId
  const periodFilterValue = assessment?.periodName

  const { assessmentQuestionnaireLinks } = useAssessmentQuestionnaireLink({
    includeAssessment: false,
    assessmentTemplateId: Number(assessmentTemplateId),
    includeQuestionnaire: false,
    enabled: Boolean(assessmentTemplateId),
  })

  const questionnaireTemplateIds = [...new Set(assessmentQuestionnaireLinks.map(item => item.questionnaireTemplateId))]

  const canFetchResponseItems = Boolean(periodFilterValue && questionnaireTemplateIds.length)

  const { items: responseItems, isLoading: responseItemIsLoading } = useFetchCollectionWithPost<ResponseItem>({
    endpoint: endpoints.responseItemsCollection,
    key: FetchKey.ResponseItemsCollection,
    payload: {
      filter: [
        {
          name: 'response.isLatestSubmitted',
          filters: [{ operator: Operators.EqualTo, value: true }],
        },
        {
          name: 'response.request.periodName',
          filters: [{ operator: Operators.EqualTo, value: periodFilterValue }],
        },
        {
          name: 'response.request.questionnaireTemplateId',
          filters: [{ operator: Operators.In, value: questionnaireTemplateIds }],
        },
      ],
      include: [
        'response.request',
        'requestItem.unit.symbol',
        'requestItem.template',
        'questionAnswerClassificationResults.questionAnswerClassificationRule',
      ],
    },
    options: {
      enabled: canFetchResponseItems,
      staleTime,
    },
  })

  const { getCircleColor, getCircleValue, getStatusText, getScoreText, getLevelDescription } =
    useAssessmentMetrics(assessment)

  const textRef = useRef<HTMLDivElement>(null)
  const [circleSize, setCircleSize] = useState(80)

  useEffect(() => {
    if (textRef.current) {
      const textWidth = textRef.current.offsetWidth
      const textHeight = textRef.current.offsetHeight
      const size = Math.max(80, Math.max(textWidth, textHeight) + 32) // 32px for padding
      setCircleSize(size)
    }
  }, [assessment])

  const isLoading = !assessment || !canFetchResponseItems || responseItemIsLoading

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    )
  }

  if (!responseItemIsLoading && responseItems && assessment) {
    return (
      <>
        <Box
          sx={{
            bgcolor: 'grey.200',
            height: '48px',
            px: 5,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/transparency/dashboard" color="inherit" underline="hover">
              {formatMessage({ id: 'general.home' })}
            </Link>
            <Typography color="text.primary">{assessment.assessmentTemplate.name}</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            pb: 4,
          }}
        >
          <Box width={800} mb={2} mt={3}>
            <Typography variant="h2">{assessment.assessmentTemplate.name}</Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              width: '800px',
              px: 4,
              py: 4,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 4,
            }}
          >
            <Box display="flex" gap={3} alignItems="flex-start">
              <Box width={83} display="flex" flexDirection="column" alignItems="center">
                <Box px={2} py={0.5} bgcolor="#FFF4F2" borderRadius="20px">
                  <Typography
                    variant="button"
                    color="text.primary"
                    sx={{
                      fontSize: 13,
                      letterSpacing: '0.4px',
                      lineHeight: '24px',
                    }}
                  >
                    {getStatusText()}
                  </Typography>
                </Box>
                <Box
                  mt={1}
                  position="relative"
                  display="inline-flex"
                  overflow="hidden"
                  sx={{
                    '& .MuiCircularProgress-root': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <Tooltip title={getScoreText()} arrow placement="top">
                    <Box sx={{ position: 'relative', width: circleSize, height: circleSize }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={circleSize}
                        thickness={6}
                        sx={{
                          color: 'grey.100',
                          opacity: 1,
                        }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={getCircleValue()}
                        size={circleSize}
                        thickness={6}
                        sx={{
                          color: getCircleColor(),
                          position: 'absolute',
                          left: 0,
                          top: 0,
                        }}
                      />
                      <Typography
                        ref={textRef}
                        variant="subtitle1"
                        component="div"
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          whiteSpace: 'nowrap',
                          padding: '0 8px',
                        }}
                      >
                        {assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment ? (
                          `L.${assessment.totalLevel}`
                        ) : (
                          <>
                            {assessment.percentage}
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              /100
                            </Box>
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
              <Box flex={1} mt={6}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {getLevelDescription(assessment.totalLevel ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <DateDisplay value={assessment?.updatedAt ?? assessment.createdAt} />
                </Typography>
              </Box>
            </Box>
          </Paper>
          <TransparencyStandardAssessmentDetailedReview
            responseItems={responseItems}
            assessment={assessment}
            maxRowsForLongAnswers={3}
          />
        </Box>
      </>
    )
  }

  return null
}

export default TransparencyStandardAssessmentPage
